import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import useToggle from '../../../Hooks/useToggle';
import BackToTop from '../../BackToTop';
import FooterHomeThree from '../../Footer/FooterHomeThree';
import HeaderNews from '../../Header/HeaderNews';
import HeroNews from '../../Header/HeroNews';
import Drawer from '../../Mobile/Drawer';

// The OTO Channel Manager, part of OTO Frontdesk. Every claim here is something the
// ChannelManager module in oto-frontdesk-api does today; keep it that way.
const FEATURES = [
    {
        icon: 'fa-inbox-in',
        title: 'Bookings arrive on their own',
        text: 'Reservations made on booking sites come into OTO Frontdesk as they happen, and become stays automatically, or after you check them.',
    },
    {
        icon: 'fa-calendar-check',
        title: 'No double bookings',
        text: 'When a room is booked, checked in or freed at your front desk, its availability is updated on every booking site you sell on.',
    },
    {
        icon: 'fa-tags',
        title: 'Rates from one place',
        text: 'Set nightly prices per room type in rate plans, and let other plans follow a base plan up or down by a percentage or an amount.',
    },
    {
        icon: 'fa-sliders-h',
        title: 'Stay restrictions',
        text: 'Set minimum and maximum stays, a minimum stay on arrival, and close dates when you do not want to sell.',
    },
    {
        icon: 'fa-undo',
        title: 'Cancellations handled',
        text: 'A cancellation on a booking site cancels the stay and frees the room. If it cannot be carried through, your team is told so someone can act.',
    },
    {
        icon: 'fa-link',
        title: 'Map once',
        text: 'Match each room type and rate on the booking sites to your rooms and rates in OTO once; from then on everything stays in step.',
    },
];

const STEPS = [
    {
        title: 'Connect',
        text: 'We connect your booking site accounts to OTO Frontdesk with you.',
    },
    {
        title: 'Map',
        text: 'Match your room types and rates on each site to the ones in OTO.',
    },
    {
        title: 'Sell',
        text: 'Bookings flow in, availability and rates flow out, and your front desk works from one calendar.',
    },
];

function ChannelManager() {
    const [drawer, drawerAction] = useToggle(false);
    return (
        <>
            <Helmet>
                <meta
                    name="description"
                    content="OTO Channel Manager connects your hotel to Booking.com, Expedia, Airbnb and Agoda from OTO Frontdesk: bookings arrive automatically, availability and rates stay in sync, and double bookings are avoided."
                />
                <title>Hotel Channel Manager for Booking.com, Expedia & Airbnb | OTO</title>
            </Helmet>
            <Drawer drawer={drawer} action={drawerAction.toggle} />
            <HeaderNews action={drawerAction.toggle} />
            <HeroNews
                title="Channel Manager"
                breadcrumb={[
                    { link: '/', title: 'Home' },
                    { link: '/apps', title: 'Apps' },
                    { link: '/apps/channel-manager', title: 'Channel Manager' },
                ]}
            />

            <div className="container pt-90 pb-90" style={{ fontSize: '18px', lineHeight: '29px' }}>
                <h3 className="mb-4">Sell your rooms everywhere, from one calendar</h3>
                <p className="mb-3">
                    OTO Channel Manager connects <Link to="/apps/frontdesk">OTO Frontdesk</Link> to
                    the booking sites your guests use, including Booking.com, Expedia, Airbnb and
                    Agoda.
                </p>
                <p className="mb-3">
                    Bookings come straight into your front desk, and every room you sell, check in
                    or free is reflected on every site. Your team stops copying reservations by
                    hand, and you stop selling the same room twice.
                </p>
                <p>
                    It is part of OTO Frontdesk, so channel bookings land on the same calendar,
                    folios and reports as your walk-ins and direct bookings, and charges from your
                    restaurant and bar through OTO POS follow the guest.
                </p>
            </div>

            <section className="appie-services-2-area bg-light pt-90 pb-55" id="service">
                <div className="container">
                    <div className="row align-items-end">
                        <div className="col-lg-6 col-md-8">
                            <div className="appie-section-title">
                                <h3 className="appie-title">Features</h3>
                                <p>What the Channel Manager does for your property.</p>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        {FEATURES.map((feature, i) => (
                            <div className="col-lg-4 col-md-6" key={feature.title}>
                                <div
                                    className={`appie-single-service-2 appie-single-service-about item-${
                                        (i % 4) + 1
                                    } mt-30`}
                                >
                                    <div className="icon">
                                        <i className={`fal ${feature.icon}`}></i>
                                    </div>
                                    <h4 className="title">{feature.title}</h4>
                                    <p>{feature.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="container pt-90 pb-60">
                <h3 className="mb-4">How it works</h3>
                <div className="row">
                    {STEPS.map((step, i) => (
                        <div className="col-md-4 mb-4" key={step.title}>
                            <h4 className="mb-2">
                                {i + 1}. {step.title}
                            </h4>
                            <p>{step.text}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="container pb-80 text-center">
                <div className="mx-auto" style={{ maxWidth: '800px' }}>
                    <p className="mb-4">
                        Want your rooms on the booking sites without the double work? Talk to us and
                        we will set up the Channel Manager with you.
                    </p>
                    <Link className="main-btn" to="/contact">
                        Ask for a demo
                    </Link>
                </div>
            </div>

            <FooterHomeThree className="appie-footer-about-area" />
            <BackToTop />
        </>
    );
}

export default ChannelManager;
